/**
 * Start screen — the intent-first front door (PRD-start-intents §5).
 *
 * Top to bottom: the question ("What are you trying to do?"), the
 * recommendation moment when they answer it, then the intent catalog as
 * cards. Every card lands in a working, job-shaped flow with a preset
 * applied — never a raw B-Mode form dressed up as a consumer card.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { getAppDefinition } from '../registry';
import { AppShell, ScreenHeading } from '../components/AppShell';
import { AppCard, Eyebrow, PrimaryButton, SecondaryButton } from '../components/primitives';
import {
  START_GROUP_LABELS,
  START_POINTS,
  type StartEntry,
  type StartGroup,
} from '@/lib/navigator/catalog';
import { launchStartPoint, type StartExtraction } from './launchStartPoint';
import {
  recommendDeterministic,
  refineRecommendation,
  type StartRecommendation,
} from './startGuideService';

const PLACEHOLDERS = [
  'e.g. we need to pick between four CRM vendors by Friday',
  'e.g. I have way too many ideas and no list',
  'e.g. three final candidates, panel meets Thursday',
  'e.g. can the team actually deliver the Q3 plan?',
  'e.g. what should we work on at the offsite?',
];

/** Card groups render in this order; 'org' renders as the quiet last row. */
const GROUP_ORDER: StartGroup[] = ['together', 'solo', 'plan', 'org'];

export default function StartScreen() {
  const navigate = useNavigate();
  const app = getAppDefinition('start')!;

  const [text, setText] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [rec, setRec] = useState<StartRecommendation | null>(null);
  const [asked, setAsked] = useState(false);
  const [aiPending, setAiPending] = useState(false);
  const [launching, setLaunching] = useState(false);
  // Guards a stale AI refinement from overwriting a newer question.
  const askSeq = useRef(0);

  useEffect(() => {
    const id = window.setInterval(
      () => setPlaceholderIndex((i) => (i + 1) % PLACEHOLDERS.length),
      4000
    );
    return () => window.clearInterval(id);
  }, []);

  const launchIntent = useCallback(
    async (intent: StartEntry, extraction?: StartExtraction | null) => {
      setLaunching(true);
      try {
        await launchStartPoint(intent, navigate, extraction);
      } finally {
        setLaunching(false);
      }
    },
    [navigate]
  );

  const handleAsk = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const seq = ++askSeq.current;
    setAsked(true);
    setRec(recommendDeterministic(trimmed));
    setAiPending(true);
    void refineRecommendation(trimmed).then((refined) => {
      if (askSeq.current !== seq) return;
      setAiPending(false);
      if (refined) setRec(refined);
    });
  }, [text]);

  const dismissRec = useCallback(() => {
    askSeq.current++;
    setRec(null);
    setAsked(false);
    setAiPending(false);
  }, []);

  const sessionPreset =
    rec && rec.intent.destination.kind === 'session' ? rec.intent.destination.preset : null;

  return (
    <AppShell app={app}>
      <ScreenHeading
        title="What are you trying to do?"
        subtitle="Say it in your own words, or pick it from the list — you'll land in the right place with the setup already done."
      />

      {/* The question */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAsk();
          }}
          placeholder={PLACEHOLDERS[placeholderIndex]}
          autoFocus
          className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
        />
        <PrimaryButton onClick={handleAsk} disabled={!text.trim() || launching}>
          <Sparkles className="h-4 w-4" /> Point me
        </PrimaryButton>
      </div>

      {/* The recommendation moment */}
      {asked ? (
        <div className="mt-4">
          {rec ? (
            <AppCard>
              <Eyebrow>Our read</Eyebrow>
              <p className="mt-2 font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                {rec.intent.title}
              </p>
              <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                {rec.why ?? rec.intent.summary}
              </p>

              {/* What we heard — carried into the destination, editable there */}
              {rec.extraction ? (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {rec.extraction.suggestedTitle ? (
                    <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-1 font-body text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                      {rec.extraction.suggestedTitle}
                    </span>
                  ) : null}
                  {(rec.extraction.options ?? []).map((option) => (
                    <span
                      key={option}
                      className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 font-body text-xs text-gray-700 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]"
                    >
                      {option}
                    </span>
                  ))}
                  <span className="font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                    — carried in for you, editable there
                  </span>
                </div>
              ) : null}

              {sessionPreset?.criteriaHints?.length ? (
                <p className="mt-2 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                  Worth weighing: {sessionPreset.criteriaHints.join(' · ')}
                </p>
              ) : null}

              {aiPending ? (
                <p className="mt-2 font-body text-xs text-gray-400 dark:text-[var(--text-ter)]">
                  Reading your description for anything else to carry in…
                </p>
              ) : null}

              <div className="mt-4 flex flex-wrap gap-2">
                <PrimaryButton
                  onClick={() => void launchIntent(rec.intent, rec.extraction)}
                  disabled={launching}
                >
                  Start — {rec.intent.title.toLowerCase()}
                  <ArrowRight className="h-4 w-4" />
                </PrimaryButton>
                <SecondaryButton onClick={dismissRec}>Show everything instead</SecondaryButton>
              </div>
            </AppCard>
          ) : (
            <AppCard>
              <p className="font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
                {aiPending
                  ? 'Reading your description…'
                  : 'No confident match — pick the closest starting point below, or say a bit more about the situation.'}
              </p>
            </AppCard>
          )}
        </div>
      ) : null}

      {/* The catalog */}
      <div className="mt-8 space-y-6">
        {GROUP_ORDER.map((group) => {
          const intents = START_POINTS.filter((i) => i.group === group);
          if (intents.length === 0) return null;

          if (group === 'org') {
            // Quiet row: honest, undressed, at the bottom.
            return (
              <div key={group}>
                <Eyebrow>{START_GROUP_LABELS[group]}</Eyebrow>
                <div className="mt-2 space-y-2">
                  {intents.map((intent) => (
                    <button
                      key={intent.id}
                      type="button"
                      disabled={launching}
                      onClick={() => void launchIntent(intent)}
                      className="flex w-full items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left hover:border-gray-300 disabled:opacity-50 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                          {intent.title}
                        </p>
                        <p className="font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                          {intent.outcomeLine}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          return (
            <div key={group}>
              <Eyebrow>{START_GROUP_LABELS[group]}</Eyebrow>
              <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {intents.map((intent) => {
                  const Icon = intent.icon;
                  return (
                    <AppCard
                      key={intent.id}
                      onClick={launching ? undefined : () => void launchIntent(intent)}
                    >
                      <div className="flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-[var(--surface-raised)]">
                          <Icon className="h-4 w-4 text-gray-600 dark:text-[var(--text-sec)]" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                            {intent.title}
                          </p>
                          <p className="mt-0.5 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                            {intent.summary}
                          </p>
                          <p className="mt-2 font-body text-xs text-alpine-green-700 dark:text-alpine-green-300">
                            {intent.outcomeLine}
                          </p>
                        </div>
                      </div>
                    </AppCard>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
